import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { cn, formatCurrency, formatPercent } from '../../lib/utils';
import { Search, Shield, Settings, Plus, CheckCircle2, AlertTriangle, FileText, Edit, Trash2, MoreHorizontal } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

const INITIAL_PAYERS = [
  { id: 1, name: 'Blue Cross Blue Shield', payerId: 'BCBS001', type: 'Commercial', status: 'active', claimsYTD: 642, revenueYTD: 1845000, denialRate: 3.2, avgDays: 18 },
  { id: 2, name: 'Medicare', payerId: 'MCARE01', type: 'Government', status: 'active', claimsYTD: 785, revenueYTD: 1980000, denialRate: 1.8, avgDays: 14 },
  { id: 3, name: 'Aetna', payerId: 'AETNA01', type: 'Commercial', status: 'active', claimsYTD: 512, revenueYTD: 1285000, denialRate: 4.1, avgDays: 22 },
  { id: 4, name: 'UnitedHealthcare', payerId: 'UHC0001', type: 'Commercial', status: 'active', claimsYTD: 578, revenueYTD: 1620000, denialRate: 2.8, avgDays: 16 },
  { id: 5, name: 'Cigna', payerId: 'CIGNA01', type: 'Commercial', status: 'active', claimsYTD: 398, revenueYTD: 875000, denialRate: 3.5, avgDays: 20 },
  { id: 6, name: 'Medicaid', payerId: 'MCAID01', type: 'Government', status: 'active', claimsYTD: 345, revenueYTD: 625000, denialRate: 2.9, avgDays: 28 },
];

const INITIAL_RULES = [
  { id: 1, name: 'Prior Auth Required - 87880', payer: 'Blue Cross', type: 'Authorization', condition: 'CPT 87880 > 3 per year', action: 'Require prior authorization', status: 'active' },
  { id: 2, name: 'Modifier 25 Required', payer: 'All Payers', type: 'Coding', condition: 'E/M + Procedure same DOS', action: 'Auto-add modifier 25 to E/M', status: 'active' },
  { id: 3, name: 'Medicare LCD: J06.9', payer: 'Medicare', type: 'Medical Necessity', condition: 'CPT 99214 with Dx J06.9', action: 'Verify documentation supports level', status: 'active' },
  { id: 4, name: 'Timely Filing Alert', payer: 'Aetna', type: 'Filing', condition: 'DOS > 75 days', action: 'Flag for urgent submission', status: 'active' },
  { id: 5, name: 'CCI Edit: 36415', payer: 'All Payers', type: 'Bundling', condition: '36415 + panel codes', action: 'Check CCI bundling rules', status: 'active' },
];

const RevQPayersRules = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('payers');
  const [payers, setPayers] = useState(INITIAL_PAYERS);
  const [rules, setRules] = useState(INITIAL_RULES);

  // Add Payer modal
  const [showAddPayer, setShowAddPayer] = useState(false);
  const [payerForm, setPayerForm] = useState({ name: '', payerId: '', type: 'Commercial' });

  // Create Rule modal
  const [showCreateRule, setShowCreateRule] = useState(false);
  const [ruleForm, setRuleForm] = useState({ name: '', payer: 'All Payers', type: 'Coding', condition: '', action: '' });

  const handleAddPayer = () => {
    if (!payerForm.name.trim() || !payerForm.payerId.trim()) {
      toast.error('Please fill in payer name and ID');
      return;
    }
    setPayers(prev => [...prev, {
      id: Date.now(),
      name: payerForm.name,
      payerId: payerForm.payerId,
      type: payerForm.type,
      status: 'active',
      claimsYTD: 0,
      revenueYTD: 0,
      denialRate: 0,
      avgDays: 0,
    }]);
    toast.success(`Payer "${payerForm.name}" added`);
    setPayerForm({ name: '', payerId: '', type: 'Commercial' });
    setShowAddPayer(false);
  };

  const handleCreateRule = () => {
    if (!ruleForm.name.trim() || !ruleForm.condition.trim() || !ruleForm.action.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setRules(prev => [...prev, {
      id: Date.now(),
      name: ruleForm.name,
      payer: ruleForm.payer,
      type: ruleForm.type,
      condition: ruleForm.condition,
      action: ruleForm.action,
      status: 'active',
    }]);
    toast.success(`Rule "${ruleForm.name}" created`);
    setRuleForm({ name: '', payer: 'All Payers', type: 'Coding', condition: '', action: '' });
    setShowCreateRule(false);
  };

  const handleToggleRule = (ruleId) => {
    setRules(prev => prev.map(r =>
      r.id === ruleId ? { ...r, status: r.status === 'active' ? 'inactive' : 'active' } : r
    ));
  };

  const handleDeleteRule = (rule) => {
    setRules(prev => prev.filter(r => r.id !== rule.id));
    toast.success(`Rule "${rule.name}" deleted`);
  };

  return (
    <div className="space-y-6" data-testid="payers-rules-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Payers & Rules Engine</h1>
          <p className="text-slate-500 mt-1">Manage payer contracts and billing rules</p>
        </div>
        <Button className="bg-black hover:bg-gray-800 gap-2" onClick={() => setShowAddPayer(true)} data-testid="add-payer-btn">
          <Plus className="h-4 w-4" /> Add Payer
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="payers" className="gap-2"><Shield className="h-4 w-4" /> Payers</TabsTrigger>
          <TabsTrigger value="rules" className="gap-2"><Settings className="h-4 w-4" /> Rules Engine</TabsTrigger>
        </TabsList>

        {/* Payers Tab */}
        <TabsContent value="payers" className="space-y-6">
          <Card>
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Search payers..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="payers-search" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Configured Payers ({payers.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Payer Name</TableHead>
                    <TableHead>Payer ID</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Claims YTD</TableHead>
                    <TableHead className="text-right">Revenue YTD</TableHead>
                    <TableHead className="text-right">Denial Rate</TableHead>
                    <TableHead className="text-right">Avg Days</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payers.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase())).map((payer) => (
                    <TableRow key={payer.id} className="hover:bg-slate-50" data-testid={`payer-row-${payer.id}`}>
                      <TableCell className="font-medium">{payer.name}</TableCell>
                      <TableCell className="font-mono text-sm">{payer.payerId}</TableCell>
                      <TableCell><Badge variant="outline">{payer.type}</Badge></TableCell>
                      <TableCell className="text-right">{payer.claimsYTD}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(payer.revenueYTD)}</TableCell>
                      <TableCell className="text-right">
                        <span className={cn(payer.denialRate <= 3 ? 'text-emerald-600' : payer.denialRate <= 5 ? 'text-amber-600' : 'text-red-600')}>
                          {formatPercent(payer.denialRate)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{payer.avgDays} days</TableCell>
                      <TableCell>
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Active
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`payer-menu-${payer.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>View Details</DropdownMenuItem>
                            <DropdownMenuItem>Edit Payer</DropdownMenuItem>
                            <DropdownMenuItem>View Contract</DropdownMenuItem>
                            <DropdownMenuItem>Manage Rules</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rules Tab */}
        <TabsContent value="rules" className="space-y-6">
          <div className="flex justify-end">
            <Button className="bg-black hover:bg-gray-800 gap-2" onClick={() => setShowCreateRule(true)} data-testid="create-rule-btn">
              <Plus className="h-4 w-4" /> Create Rule
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Active Rules ({rules.filter(r => r.status === 'active').length})</CardTitle>
              <CardDescription>Automated billing rules and compliance checks</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {rules.map((rule) => (
                  <div key={rule.id} className="p-4 border rounded-lg hover:bg-slate-50 transition-colors" data-testid={`rule-row-${rule.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                          <Settings className="h-5 w-5 text-slate-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold">{rule.name}</h4>
                            <Badge variant="outline" className="text-xs">{rule.type}</Badge>
                          </div>
                          <p className="text-sm text-slate-500 mb-2">Payer: {rule.payer}</p>
                          <div className="flex flex-col gap-1 text-sm">
                            <p><span className="text-slate-500">If:</span> {rule.condition}</p>
                            <p><span className="text-slate-500">Then:</span> {rule.action}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Switch checked={rule.status === 'active'} onCheckedChange={() => handleToggleRule(rule.id)} data-testid={`rule-toggle-${rule.id}`} />
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`rule-menu-${rule.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem><Edit className="h-4 w-4 mr-2" /> Edit Rule</DropdownMenuItem>
                            <DropdownMenuItem><FileText className="h-4 w-4 mr-2" /> View History</DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600" onSelect={() => handleDeleteRule(rule)}>
                              <Trash2 className="h-4 w-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card><CardContent className="p-4"><div className="flex items-center gap-3"><Shield className="h-5 w-5 text-blue-600" /><div><p className="font-medium">Authorization Rules</p><p className="text-sm text-slate-500">Prior auth requirements</p></div></div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center gap-3"><FileText className="h-5 w-5 text-violet-600" /><div><p className="font-medium">Coding Rules</p><p className="text-sm text-slate-500">Modifiers & edits</p></div></div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center gap-3"><AlertTriangle className="h-5 w-5 text-amber-600" /><div><p className="font-medium">Compliance Rules</p><p className="text-sm text-slate-500">LCD/NCD policies</p></div></div></CardContent></Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Payer Modal */}
      <Dialog open={showAddPayer} onOpenChange={setShowAddPayer}>
        <DialogContent className="max-w-md" data-testid="add-payer-modal">
          <DialogHeader>
            <DialogTitle>Add Payer</DialogTitle>
            <DialogDescription>Add a new insurance payer to your configuration</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="payer-name">Payer Name</Label>
              <Input id="payer-name" data-testid="payer-name-input" placeholder="e.g. Humana" value={payerForm.name} onChange={(e) => setPayerForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payer-id">Payer ID</Label>
              <Input id="payer-id" data-testid="payer-id-input" placeholder="e.g. HUM001" value={payerForm.payerId} onChange={(e) => setPayerForm(f => ({ ...f, payerId: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Payer Type</Label>
              <Select value={payerForm.type} onValueChange={(v) => setPayerForm(f => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="payer-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                  <SelectItem value="Government">Government</SelectItem>
                  <SelectItem value="Workers Comp">Workers Comp</SelectItem>
                  <SelectItem value="Auto">Auto / No-Fault</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPayer(false)}>Cancel</Button>
            <Button onClick={handleAddPayer} data-testid="submit-payer-btn" className="bg-black hover:bg-gray-800 gap-2">
              <Plus className="h-4 w-4" /> Add Payer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Rule Modal */}
      <Dialog open={showCreateRule} onOpenChange={setShowCreateRule}>
        <DialogContent className="max-w-md" data-testid="create-rule-modal">
          <DialogHeader>
            <DialogTitle>Create Rule</DialogTitle>
            <DialogDescription>Define an automated billing rule</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rule-name">Rule Name</Label>
              <Input id="rule-name" data-testid="rule-name-input" placeholder="e.g. Prior Auth for MRI" value={ruleForm.name} onChange={(e) => setRuleForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Payer</Label>
                <Select value={ruleForm.payer} onValueChange={(v) => setRuleForm(f => ({ ...f, payer: v }))}>
                  <SelectTrigger data-testid="rule-payer-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All Payers">All Payers</SelectItem>
                    {payers.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Rule Type</Label>
                <Select value={ruleForm.type} onValueChange={(v) => setRuleForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="rule-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Authorization">Authorization</SelectItem>
                    <SelectItem value="Coding">Coding</SelectItem>
                    <SelectItem value="Medical Necessity">Medical Necessity</SelectItem>
                    <SelectItem value="Filing">Filing</SelectItem>
                    <SelectItem value="Bundling">Bundling</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rule-condition">Condition (If)</Label>
              <Input id="rule-condition" data-testid="rule-condition-input" placeholder="e.g. CPT 70553 without auth" value={ruleForm.condition} onChange={(e) => setRuleForm(f => ({ ...f, condition: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rule-action">Action (Then)</Label>
              <Input id="rule-action" data-testid="rule-action-input" placeholder="e.g. Block submission, require auth" value={ruleForm.action} onChange={(e) => setRuleForm(f => ({ ...f, action: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateRule(false)}>Cancel</Button>
            <Button onClick={handleCreateRule} data-testid="submit-rule-btn" className="bg-black hover:bg-gray-800 gap-2">
              <Plus className="h-4 w-4" /> Create Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RevQPayersRules;
